import { DatabaseApiKeyService } from '@qdrant/qdrant-cloud-public-api/cluster/auth/v2';
import { ClusterService } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { LegalDocumentType } from '@qdrant/qdrant-cloud-public-api/iam/v1';
import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { isAccountMissingError } from '@/api/errors';
import { accountService } from '@/api/services/account';
import { databaseApiKeyV1Service } from '@/api/services/authentication';
import { clusterService } from '@/api/services/cluster';
import { databaseService } from '@/api/services/database';
import { databaseApiKeyV2Service } from '@/api/services/database-api-key';
import { iamService } from '@/api/services/iam';
import { paymentService } from '@/api/services/payment';
import { quotaService } from '@/api/services/quota';
import { clearSentryAccountTag, setSentryAccountTag } from '@/helpers/errors/identity';
import { safeAsync } from '@/helpers/errors/safeAsync';
import { ensureStatsigUserFeatureGateReady } from '@/helpers/statsig/featureGate/ensureStatsigUserFeatureGateReady';
import { resolvePaymentSession } from '@/router/resolvers/payment';
import {
  resolveClusterRouteEntrypoint,
  resolveAccount,
  resolveAccountHasRpcPermissions,
} from '@/router/resolvers/user';
import { accountSearchSchema, validateParams } from '@/router/utils';
import { accountIdUrlParam } from './route.hooks';

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId')({
  validateSearch: accountSearchSchema,
  params: validateParams(z.object({ accountId: accountIdUrlParam })),
  // Default owner for the whole per-account area (dashboard, settings, get-started, …). Subtrees with
  // a clearer owner — clusters, hybrid-cloud, cloud-access, billing — override it deeper in the tree.
  staticData: {
    domain: 'account',
  },
  async beforeLoad({ context, params, search, matches, preload }) {
    const { accountId } = params;

    await ensureStatsigUserFeatureGateReady(context);

    // Tag before any account-scoped fetches run, so errors thrown from loaders below this route
    // already carry the account. The tag is cleared in `onLeave` when leaving account scope.
    if (!preload) {
      setSentryAccountTag(accountId);
    }

    const { queryClient } = context;

    // The account id has to be one the user holds. On the leaf match that is a redirect; deeper down
    // the error is left to this route's own not-found boundary, which names the missing account.
    // `_authenticated` resolves the same account for Statsig, but swallows a missing one: this is its owner.
    const isLeafMatch = matches.at(-1)?.routeId === Route.id;
    const [error] = await safeAsync(resolveAccount({ accountId, ...context }));
    if (error) {
      if (isLeafMatch && isAccountMissingError(error)) {
        const fallbackId = error.fallbackAccountId;
        if (!fallbackId) {
          // No accounts exist for the user, redirect to the accounts list page to let them create one.
          throw redirect({ to: '/accounts', search, replace: true });
        }
        // Redirect to the fallback account if the requested account is missing.
        throw redirect({
          to: '/accounts/$accountId',
          params: { accountId: accountIdUrlParam.parse(fallbackId) },
          search,
          replace: true,
        });
      }
      throw error;
    }
    if (isLeafMatch) {
      // If the user is on the account dashboard, redirect to the most appropriate account page.
      await resolveClusterRouteEntrypoint({ accountId, ...context });
    }

    // Account permissions must be in cache before any descendant route or component issues a
    // permission-gated RPC: the permissions interceptor reads them synchronously from the cache.
    // beforeLoad resolves before all descendant loaders run, so awaiting here is the barrier that
    // guarantees that ordering for every account-scoped route below $accountId.
    const listEffectivePermissionsQuery = iamService.listEffectivePermissionsQuery(params);

    await queryClient.ensureQueryData(listEffectivePermissionsQuery);

    if (search.session_id) {
      await resolvePaymentSession({ queryClient, accountId, sessionId: search.session_id });
    }

    // The Overview stepper walks the user through creating a cluster and its first database API key,
    // so it needs every one of those four RPCs to be callable (`allOf`, the default). It runs after the
    // permissions barrier above, which is what puts the effective permissions in cache for it to read.
    const canRunOnboardingStepper = await resolveAccountHasRpcPermissions(
      [
        ClusterService.method.listClusters,
        ClusterService.method.createCluster,
        DatabaseApiKeyService.method.listDatabaseApiKeys,
        DatabaseApiKeyService.method.createDatabaseApiKey,
      ],
      { queryClient, accountId },
    );

    const results = {
      getAccountQuery: accountService.getAccountQuery(params),
      getAccountQuotasQuery: quotaService.getAccountQuotasQuery(params),
      getAuthenticatedUserQuotasQuery: quotaService.getAuthenticatedUserQuotasQuery(),
      listEffectivePermissionsQuery,
      listQdrantReleasesQueryOptions: databaseService.listQdrantReleasesQuery(params),
      userConsentPrivacyPolicyQuery: iamService.getUserConsentQuery({
        documentType: LegalDocumentType.PRIVACY_POLICY,
      }),
      listPaymentMethodsQuery: paymentService.listPaymentMethodsQuery({ accountId }),
      clustersByAccountIdQuery: clusterService.listClustersQuery({ accountId }),
    };

    // The design for a partially-permitted stepper needs to be thought out more, so it is all or nothing.
    if (!canRunOnboardingStepper) {
      return {
        ...results,
        listAccountDbApiKeysV1Query: null,
        listAccountDbApiKeysV2Query: null,
        listClusterDbApiKeysQuery: null,
      };
    }

    return {
      ...results,
      listAccountDbApiKeysV1Query: databaseApiKeyV1Service.listDatabaseApiKeysQuery({ accountId }),
      listAccountDbApiKeysV2Query: databaseApiKeyV2Service.listDatabaseApiKeysQuery({ accountId }),
      listClusterDbApiKeysQuery: ({ clusterId }: { clusterId: string }) =>
        databaseApiKeyV2Service.listDatabaseApiKeysQuery({ accountId, clusterId }),
    };
  },
  loader: ({
    context: {
      queryClient,
      getAccountQuery,
      getAuthenticatedUserQuotasQuery,
      getAccountQuotasQuery,
      listQdrantReleasesQueryOptions,
      userConsentPrivacyPolicyQuery,
    },
  }) => {
    const mandatoryRequests = Promise.all([
      queryClient.ensureQueryData(getAccountQuery),
      queryClient.fetchQuery(userConsentPrivacyPolicyQuery),
    ]);
    // Started here, not awaited: the releases list is read with `useSuspenseQuery` by the clusters
    // list, the cluster version picker and the creation form, so warming it at navigation time
    // means one shared request instead of one per sibling read. The account pages that never
    // render it (settings, billing, get-started) must not wait for it.
    void Promise.all([
      queryClient.prefetchQuery(getAuthenticatedUserQuotasQuery),
      queryClient.prefetchQuery(getAccountQuotasQuery),
      queryClient.prefetchQuery(listQdrantReleasesQueryOptions),
    ]);
    return mandatoryRequests;
  },
  // Clear the account tag set in `beforeLoad` when leaving account scope. `onLeave` fires only when
  // this route's `routeId` is no longer matched (i.e. navigating to a non-account route); switching
  // between accounts ($accountId A → B) is a staying match, so the tag is re-set by `beforeLoad`.
  onLeave: () => clearSentryAccountTag(),
  component: lazyRouteComponent(() => import('./route.component'), 'AccountComponent'),
  errorComponent: lazyRouteComponent(() => import('./route.component'), 'AccountErrorFallback'),
  notFoundComponent: lazyRouteComponent(() => import('./route.component'), 'AccountNotFoundFallback'),
});
